// The Feast (docs/04 §17.5): Hold & Spin on the whole grid. Only Morsels and blanks spin; every
// Morsel that lands locks and the respins reset. Forks collect, Peppers double their neighbours,
// and a full grid is THE FEAST.
import { B } from '../../content/balance';
import { D, type Decimal } from '../decimal';
import type { Modifiers } from '../modifiers';
import type { Rng } from '../rng';
import type { FeastBonus, Morsel, MorselKind } from '../types';

function rollMorsel(rng: Rng, plug: boolean): Morsel {
  if (plug) return { kind: 'plug', value: 0 };
  const kinds = Object.keys(B.FEAST_KINDS) as MorselKind[];
  const kind = kinds[rng.weighted(kinds.map((k) => B.FEAST_KINDS[k]!))] ?? 'morsel';
  if (kind === 'morsel') return { kind, value: rng.pick(B.FEAST_MORSEL_VALUES) };
  return { kind, value: B.FEAST_VALUES[kind] ?? 0 };
}

export function createFeast(rng: Rng, par: Decimal, reels: number, rows: number, mods: Modifiers): FeastBonus {
  const cells: (Morsel | null)[] = Array.from({ length: reels * rows }, () => null);
  const b: FeastBonus = {
    mode: 'feast',
    par,
    reels,
    rows,
    cells,
    respins: mods.feastRespins,
    maxRespins: mods.feastRespins,
    spins: 0,
    landed: [],
    full: false,
    total: 0,
    done: false,
    payout: D(0),
    voided: false,
  };
  // Silver Fork: the table is laid with a couple of morsels already.
  for (let i = 0; i < mods.feastStartMorsels; i++) {
    const empty = cells.map((c, k) => (c ? -1 : k)).filter((k) => k >= 0);
    if (empty.length) cells[rng.pick(empty)] = { kind: 'morsel', value: rng.pick(B.FEAST_MORSEL_VALUES) };
  }
  return b;
}

/** One respin. `plugLive`: the Plug may be served (once, good path). */
export function feastSpin(b: FeastBonus, rng: Rng, plugLive: boolean): void {
  if (b.done) return;
  b.spins++;
  b.landed = [];
  let plugServed = b.cells.some((c) => c?.kind === 'plug');
  b.cells.forEach((c, i) => {
    if (c || !rng.chance(B.FEAST_LAND)) return;
    const plug = plugLive && !plugServed && rng.chance(B.FEAST_PLUG_CHANCE);
    if (plug) plugServed = true;
    b.cells[i] = rollMorsel(rng, plug);
    b.landed.push(i);
  });
  b.full = b.cells.every(Boolean);
  if (b.landed.length) b.respins = b.maxRespins;
  else b.respins--;
  if (b.full || b.respins <= 0) finishFeast(b);
}

export function feastValue(b: FeastBonus): number {
  const at = (x: number, y: number) => (x < 0 || y < 0 || x >= b.reels || y >= b.rows ? null : b.cells[y * b.reels + x]);
  const values = b.cells.map((c) => c?.value ?? 0);
  // Peppers: ×2 on adjacent morsels.
  b.cells.forEach((c, i) => {
    if (c?.kind !== 'pepper') return;
    const x = i % b.reels;
    const y = Math.floor(i / b.reels);
    for (let dy = -1; dy <= 1; dy++)
      for (let dx = -1; dx <= 1; dx++) {
        if (!dx && !dy) continue;
        const n = at(x + dx, y + dy);
        if (n && n.kind !== 'pepper' && n.kind !== 'fork') values[(y + dy) * b.reels + x + dx]! *= 2;
      }
  });
  // Forks: each collects every other morsel's value on top.
  const plain = values.reduce((a, v) => a + v, 0);
  const forks = b.cells.filter((c) => c?.kind === 'fork').length;
  let total = plain + forks * plain;
  if (b.full) total += B.FEAST_FULL;
  return total;
}

function finishFeast(b: FeastBonus): void {
  b.total = feastValue(b);
  b.done = true;
  b.payout = b.par.mul(b.total).floor();
}

export function feastAuto(b: FeastBonus, rng: Rng, plugLive: boolean): void {
  let guard = 0;
  while (!b.done && guard++ < 500) feastSpin(b, rng, plugLive);
}
